import { useContext } from "react";

import OrderTotals from "~/features/cart/cart/components/OrderTotals";
import Spacer from "~/common/components/Spacer";
import { useDispatch } from "react-redux";
import ProductTable from "~/features/cart/activeQuote/components/productTable/ProductTable";
import { type CartGrouping } from "~/features/cart/cart/models/CartData";
import { type RootState } from "~/app/store";
import { useTranslation } from "react-i18next";
import { resolveUrl } from "~/utils/url/urlResolver";
import { selectLineItem } from "~/features/cart/cart/redux/selectors/cartSelectors";
import { selectSalesmanCheckoutCart } from "~/features/cart/cart/redux/selectors/checkoutPageSelectors";
import { Button, Stack, Typography } from "~/common/mui";
import CheckoutSectionTitle from "~/features/cart/checkout/components/CheckoutSectionTitle";
import { requestConfirmation } from "~/common/utils/ModalService";
import ConfirmationModal from "~/common/components/ConfirmationModal";
import { FccAction, trackFccAction } from "~/features/freeCapacityPush/api/trackFccAction";

import classes from "~/features/cart/activeQuote/components/ActiveQuoteReview/ActiveQuoteReview.module.less";
import { ActiveQuotePageModeContext } from "~/features/cart/activeQuote/components/ActiveQuotePage/ActiveQuotePage";
import { Mode } from "~/features/cart/cart/models/QuoteData";
import { scrollToTop } from "~/common/utils/misc";
import { Alert } from "~/common/mui/Alert/Alert";
import { AnalyticsMQ } from "~/app/analytics/AnalyticsMQ/AnalyticsMQ";
import type { AnyAction } from "~/app/store/types";
import type { ThunkAction } from "redux-thunk";
import { selectProductDescriptor } from "~/domains/product/redux/selectors";
import { type ActiveQuoteViewModel } from "~/features/cart/activeQuote/hooks/useActiveQuoteViewModel";

interface Props {
  viewModel: ActiveQuoteViewModel;
}

const ActiveQuoteReview = (props: Props) => {
  const { viewModel } = props;
  const { totalWeight, totalSum, validCart } = viewModel.aggregates;
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const setMode = useContext(ActiveQuotePageModeContext);

  const fccViewModel = viewModel.reviewOrderablesViewModel;
  const nonFccViewModel = viewModel.reviewQuotablesViewModel;

  const isMixedFcc = fccViewModel.entries.length > 0 && nonFccViewModel.entries.length > 0;

  const trackPlaceOrder = (isFccOnly: boolean) => {
    trackFccAction(isFccOnly ? FccAction.CreateQuoteForFcc : FccAction.CreateQuoteForAll);
    dispatch(trackPlaceOrderThunk(isFccOnly));
  };

  const submitOnlyFcc = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const url = e.currentTarget.href;
    const confirmation = await requestConfirmation(({ onConfirm, onCancel }) => (
      <ConfirmationModal
        label={
          <Stack gap={4}>
            <Typography variant={"bodyMedium"}>
              <b>{t("fcc.checkout.confirmation.title")}</b>
            </Typography>
            <Typography variant={"bodyMedium"}>{t("fcc.checkout.confirmation.content")}</Typography>
          </Stack>
        }
        okLabel={t("fcc.checkout.confirmation.yes")}
        cancelLabel={t("fcc.checkout.confirmation.no")}
        onCancel={onCancel}
        onOk={onConfirm}
      />
    ));
    if (confirmation) {
      trackPlaceOrder(true);
      window.location.href = url;
    } else {
      setMode(Mode.EDIT);
      scrollToTop();
    }
  };

  const buttons = (
    <>
      {isMixedFcc && (
        <Button
          data-test-id={"btn-place-quote-fcc-only"}
          color={"secondary"}
          disabled={!validCart}
          onClick={submitOnlyFcc}
          href={resolveUrl("/quote/confirm?fccOnly=true")}
        >
          {t("fcc.checkout.btn.fccOnly")}
        </Button>
      )}
      <Button
        data-test-id={"btn-place-quote"}
        disabled={!validCart}
        href={resolveUrl("/quote/confirm")}
        onClick={() => trackPlaceOrder(false)}
      >
        {t(isMixedFcc ? "fcc.checkout.btn.mixed" : "activeQuotePage.review.send")}
      </Button>
    </>
  );

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }} className={"mb-30"}>
        <Alert severity={"info"}>{t("activeQuotePage.review.summary")}</Alert>
        <Stack direction={"row"} gap={2}>
          {buttons}
        </Stack>
      </div>

      {fccViewModel.entries.length > 0 && (
        <>
          <CheckoutSectionTitle>
            {`Items to be requested - Free Capacity scope (${fccViewModel.entries.length})`}
          </CheckoutSectionTitle>
          <ProductTable tableViewModel={fccViewModel} />
        </>
      )}
      {nonFccViewModel.entries.length > 0 && (
        <>
          <CheckoutSectionTitle>
            {t("cartPage.header.toBeQuoted", { count: nonFccViewModel.entries.length })}
          </CheckoutSectionTitle>
          <ProductTable tableViewModel={nonFccViewModel} />
        </>
      )}

      <Spacer isThick={true} className={"mb-30 mt-60"} />
      <div className="row">
        <div className="col-push-6 col-xl-push-8 col-6 col-xl-4">
          <OrderTotals showPriceOnRequest={false} weight={totalWeight} totalValue={totalSum}>
            <Stack gap={4} className={isMixedFcc ? classes.bottomButtonContainerOuter : undefined}>
              <Stack direction={"row"} gap={2} className={isMixedFcc ? classes.bottomButtonContainer : undefined}>
                {buttons}
              </Stack>
              {isMixedFcc && <Alert severity={"info"}>{t("fcc.checkout.mixedMessage")}</Alert>}
            </Stack>
          </OrderTotals>
        </div>
      </div>

      <div className="d-flex flex-direction-column">
        <Alert className={"mt-30"} severity="info">
          {t("activeQuotePage.review.footerMsg1")}
        </Alert>
      </div>
    </>
  );
};

export default ActiveQuoteReview;

function trackPlaceOrderThunk(isFccOnly: boolean): ThunkAction<void, RootState, undefined, AnyAction> {
  return (_, getState) => {
    const state = getState();

    const cartData = selectSalesmanCheckoutCart(state);

    function getPackEntryIds(group: CartGrouping) {
      return group.entries.flatMap((entry) => entry.packGroups).flatMap((entry) => entry.packEntryIds);
    }
    // orderables = fcc items
    // quotables = non fcc items
    const packEntryIds = getPackEntryIds(cartData.orderables);
    if (!isFccOnly) {
      packEntryIds.push(...getPackEntryIds(cartData.quotables));
    }
    const entries = packEntryIds.map((id) => {
      const lineItem = selectLineItem(id)(state);
      const product = selectProductDescriptor(state, lineItem);
      return {
        lineItem,
        product,
      };
    });

    AnalyticsMQ.instance.push("placeOrder", {
      orderId: "N/A",
      type: "salesman-quote",
      paymentType: "",
      entries,
    });
  };
}
